export interface Company {
  id: string;
  name: string;
  industry?: string;
  size?: string;
  website?: string;
  phone?: string;
  email?: string;
  address?: string;
  logo?: string;
  description?: string;
  estado?: number;
  fecha_creacion?: string;
  fecha_actualizacion?: string;
  totalUsuarios?: number;
  totalOportunidades?: number;
  createdAt?: Date | string;
  updatedAt?: Date | string;
  contacts?: Contact[];
  opportunities?: Opportunity[];
  slug?: string;
}

export interface CustomerStatus {
  id: string;
  name: string;
  description?: string;
  color: string;
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  countryCode: string;
  position: string;
  companyId: string;
  avatar?: string;
  linkedinUrl?: string;
  createdAt: Date | string;
  updatedAt: Date | string;
  company?: Company;
  opportunities: Opportunity[];
  activities: Activity[];
  status?: string;
  statusId?: string;
  attachments?: Attachment[];
  notes?: string;
}

export interface Stage {
  id: string;
  name: string;
  description?: string;
  color: string;
  level: number;
  orden: number;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

export interface StageInput {
  name: string;
  description?: string;
  color: string;
  level: number;
  orden: number;
}

export const STAGE_LEVELS = {
  INICIAL: 0,
  EN_PROGRESO: 1,
  FINALIZADA: 2,
  CERRADA: 3,
} as const;

export const STAGE_LEVEL_LABELS: Record<number, string> = {
  0: "Inicial",
  1: "En Progreso",
  2: "Finalizada",
  3: "Cerrada",
};

export const FEATURE_KEYS = {
  COMPANIES: "companies",
  CONTACTS: "contacts",
  OPPORTUNITIES: "opportunities",

  PRODUCTS: "products",
  REPORTS: "reports",
  MULTI_USER: "multi_user",
  CUSTOM_FIELDS: "custom_fields",
  API_ACCESS: "api_access",
  ADVANCED_ANALYTICS: "advanced_analytics",
  WHITE_LABEL: "white_label",
} as const;

export type FeatureKey = (typeof FEATURE_KEYS)[keyof typeof FEATURE_KEYS];

export interface PlanFeature {
  id: string;
  key: string;
  name: string;
  description?: string;
  limit?: number;
  enabled: boolean;
}

export interface Plan {
  id: string;
  name: string;
  description?: string;
  price: number;
  billingCycle: "monthly" | "yearly";
  features: PlanFeature[];
  isActive: boolean;
  isDefault?: boolean;
  maxUsers?: number;
  maxCompanies?: number;
  maxStorage?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface PlanInput {
  name: string;
  description?: string;
  price: number;
  billingCycle: "monthly" | "yearly";
  features: PlanFeature[];
  isActive: boolean;
  maxUsers?: number;
  maxCompanies?: number;
  maxStorage?: number;
}

export interface Attachment {
  id?: string;
  url: string;
  tipo: string;
  nombre: string;
  descripcion?: string;
  tamano?: number;
  fechaSubida: string;
}

export interface AttachmentInput {
  file: File;
  description: string;
}

export interface Opportunity {
  id: string;
  title: string;
  description: string;
  value: number;
  stage: string;
  probability: number;
  expectedCloseDate: Date | string;
  companyId: string;
  contactId: string;
  source?: string;
  responsibleId?: string;
  priority?: "alta" | "media" | "baja";
  nextStep?: string;
  attachments?: Attachment[];
  company?: Company;
  contact?: Contact;
  activities: Activity[];

  createdAt: Date | string;
  updatedAt: Date | string;
}

export type OpportunityStage = string;



export interface Activity {
  id: string;
  type: ActivityType;
  title: string;
  description: string;
  date: Date | string;
  contactId?: string;
  opportunityId?: string;
  companyId?: string;
  userId?: string;
  user?: {
    id: string;
    name: string;
    avatar?: string;
  };
  metadata?: {
    fromStage?: string;
    toStage?: string;
    field?: string;
    oldValue?: any;
    newValue?: any;
  };
  createdAt: Date | string;
}

export type ActivityType =
  | "call"
  | "meeting"
  | "email"
  | "note"
  | "task"
    | "CREATE"
  | "EDIT"
  | "MOVE"
  | "COMMENT"
  | "DOCUMENT";

export interface DashboardMetrics {
  totalRevenue: number;
  totalOpportunities: number;
  totalCompanies: number;
  totalContacts: number;
  conversionRate: number;
  avgDealSize: number;
  revenueGrowth: number;
  opportunitiesByStage: Record<string, number>;
  monthlyRevenue: { month: string; revenue: number }[];
}

export interface StageTemplate {
  id: number;
  name: string;
  description?: string;
  stages: Omit<Stage, "id" | "createdAt" | "updatedAt">[];
}

export interface CompanyAdmin {
  id: string;
  name: string;
  descripcion?: string;
  logo_url?: string;
  direccion?: string;
  telefono?: string;
  email?: string;
  sitio_web?: string;
  estado: number;
  fecha_creacion: string;
  fecha_actualizacion: string;
  totalUsuarios?: number;
  totalOportunidades?: number;
}

export interface CompanyAdminInput {
  name: string;
  descripcion?: string;
  logo_url?: string;
  direccion?: string;
  telefono?: string;
  email?: string;
  sitio_web?: string;
  estado?: number;
}

export interface User {
  id: string;
  nombre: string;
  foto_url?: string;
  email: string;
  contrasena_hash?: string;
  id_rol: string;
  estado: number;
  empresa_administrativo_id: string;
  fecha_creacion: string;
  planId?: string;
  plan?: Plan;
  empresa?: Company | CompanyAdmin;
  welcomeEmailSent?: boolean;
  welcomeEmailSentAt?: string;
  canSendWelcomeEmail?: boolean;
  name?: string;

  first_name?: string;
  last_name?: string;
  phone?: string;
  is_owner?: boolean;
  avatar_url?: string;
  created_at?: string;
  updated_at?: string;
  sales_revenue?: number;
}

export interface UserInput {
  nombre: string;
  email: string;
  foto_url?: string;
  password?: string;
  confirmPassword?: string;
  id_rol: string;
  empresa_administrativo_id: string;
  estado?: number;
}

export interface EmailRequest {
  userId: string;
  emailType: "welcome" | "password_reset";
  companyId?: string;
}

export interface EmailResponse {
  success: boolean;
  message: string;
  sentAt?: string;
}

export function sortStagesByOrder(stages: Stage[]): Stage[] {
  return [...stages].sort((a, b) => {
    const ordenA = a.orden ?? 999;
    const ordenB = b.orden ?? 999;
    return ordenA - ordenB;
  });
}

export function getNextStageOrder(stages: Stage[], level?: number): number {
  let relevantStages = stages;

  if (level !== undefined) {
    relevantStages = stages.filter((s) => s.level === level);
  }

  if (relevantStages.length === 0) return 1;

  const maxOrden = Math.max(...relevantStages.map((s) => s.orden || 0));
  return maxOrden + 1;
}

export function validateStageOrder(orden: number): boolean {
  return Number.isInteger(orden) && orden > 0;
}

export function reorderStagesAfterDelete(
  stages: Stage[],
  deletedStageOrder: number,
): Stage[] {
  return stages.map((stage) => {
    if (stage.orden > deletedStageOrder) {
      return { ...stage, orden: stage.orden - 1 };
    }
    return stage;
  });
}

export function getStageLevelLabel(level: number): string {
  return STAGE_LEVEL_LABELS[level] || "";
}

export function groupStagesByLevel(stages: Stage[]): Record<number, Stage[]> {
  return stages.reduce(
    (acc, stage) => {
      if (!acc[stage.level]) {
        acc[stage.level] = [];
      }
      acc[stage.level].push(stage);
      return acc;
    },
    {} as Record<number, Stage[]>,
  );
}

export function validateAttachment(attachment: Partial<Attachment>): boolean {
  return !!(attachment.nombre && attachment.tipo && attachment.fechaSubida);
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}





export function getPriorityColor(priority?: string): string {
  switch (priority) {
    case "alta":
      return "text-red-600 bg-red-50 border-red-200";
    case "media":
      return "text-yellow-600 bg-yellow-50 border-yellow-200";
    case "baja":
      return "text-green-600 bg-green-50 border-green-200";
    default:
      return "text-slate-600 bg-slate-50 border-slate-200";
  }
}

export const DEFAULT_SOURCES = [
  "Sitio Web",
  "Referido",
  "Llamada Directa",
  "Redes Sociales",
  "Evento",
  "Campañas de marketing",
  "Otro",
] as const;

export const PRIORITIES = ["alta", "media", "baja"] as const;

export const ALLOWED_FILE_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/jpg",
  "image/gif",
  "image/webp",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
  "text/csv",
] as const;

export const MAX_FILE_SIZE = 10 * 1024 * 1024;

export function calculateStageValue(
  opportunities: Opportunity[],
  stageName: string,
): number {
  return opportunities
    .filter((opp) => opp.stage === stageName)
    .reduce((sum, opp) => sum + opp.value, 0);
}

export function calculateExpectedValue(opportunity: Opportunity): number {
  return (opportunity.value * opportunity.probability) / 100;
}

export function isOpportunityOverdue(opportunity: Opportunity): boolean {
  const closeDate = new Date(opportunity.expectedCloseDate);
  return (
    closeDate < new Date() &&
    opportunity.stage !== "ganada" &&
    opportunity.stage !== "perdida"
  );
}

export function isStage(obj: any): obj is Stage {
  return (
    obj &&
    typeof obj.id === "string" &&
    typeof obj.name === "string" &&
    typeof obj.color === "string" &&
    typeof obj.level === "number" &&
    typeof obj.orden === "number"
  );
}

export function isOpportunity(obj: any): obj is Opportunity {
  return (
    obj &&
    typeof obj.id === "string" &&
    typeof obj.title === "string" &&
    typeof obj.value === "number" &&
    typeof obj.probability === "number"
  );
}

export function isAttachment(obj: any): obj is Attachment {
  return (
    obj &&
    typeof obj.nombre === "string" &&
    typeof obj.tipo === "string" &&
    typeof obj.fechaSubida === "string"
  );
}

export function isCustomerStatus(obj: any): obj is CustomerStatus {
  return (
    obj &&
    typeof obj.id === "string" &&
    typeof obj.name === "string" &&
    typeof obj.color === "string" &&
    typeof obj.order === "number" &&
    typeof obj.isActive === "boolean"
  );
}

export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

export function isValidPhone(phone: string): boolean {
  const phoneRegex = /^[\d\s\-\+\(\)]+$/;
  return phoneRegex.test(phone) && phone.replace(/\D/g, "").length >= 8;
}

export function getInitials(nombre: string): string {
  const names = nombre.trim().split(" ");
  if (names.length === 1) {
    return names[0].substring(0, 2).toUpperCase();
  }
  return (names[0][0] + names[names.length - 1][0]).toUpperCase();
}

export function formatUserName(nombre: string): string {
  return nombre
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function formatPhone(phone: string): string {
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 8) {
    return `${cleaned.slice(0, 4)}-${cleaned.slice(4)}`;
  }
  return phone;
}

export function hasFeatureAccess(user: any, featureKey: FeatureKey): boolean {
  if (!user?.plan) return false;

  const feature = user.plan.features.find(
    (f: PlanFeature) => f.key === featureKey,
  );
  return feature?.enabled ?? false;
}

export function getFeatureLimit(
  user: any,
  featureKey: FeatureKey,
): number | null {
  if (!user?.plan) return null;

  const feature = user.plan.features.find(
    (f: PlanFeature) => f.key === featureKey,
  );
  return feature?.limit ?? null;
}

export function canCreateCompany(user: any, currentCount: number): boolean {
  if (!user?.plan) return false;

  const maxCompanies = user.plan.maxCompanies;
  if (maxCompanies === undefined) return true;

  return currentCount < maxCompanies;
}

export function canCreateUser(user: any, currentCount: number): boolean {
  if (!user?.plan) return false;

  const maxUsers = user.plan.maxUsers;
  if (maxUsers === undefined) return true;

  return currentCount < maxUsers;
}

export function getUsagePercentage(current: number, limit: number): number {
  if (limit === 0) return 0;
  return Math.min((current / limit) * 100, 100);
}

export function isNearLimit(current: number, limit: number): boolean {
  if (limit === 0) return false;
  return current / limit >= 0.8;
}

export function isLimitReached(current: number, limit: number): boolean {
  return current >= limit;
}

export function getLimitStatusBadge(current: number, limit: number): string {
  if (isLimitReached(current, limit)) {
    return "bg-red-100 text-red-800 border-red-300";
  }
  if (isNearLimit(current, limit)) {
    return "bg-yellow-100 text-yellow-800 border-yellow-300";
  }
  return "bg-green-100 text-green-800 border-green-300";
}

export interface ApiResponse<T = any> {
  statusCode: number;
  body: {
    success: boolean;
    data: T;
    message?: string;
  };
}

export interface ErrorResponse {
  statusCode: number;
  body: {
    success: boolean;
    error: string;
    message: string;
  };
}
